import { createContext, useContext } from "react";
import type { ReactNode } from "react";
import { createPortal } from "react-dom";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import CloseIcon from "@mui/icons-material/Close";
import { toEdgeShadow } from "@/app/shadow.utils";

const SIDE_PANEL_WIDTH = 300;

/** The slot element RootLayout keeps between the side menu and the page, where SidePanels are portalled. */
export const SidePanelSlotContext = createContext<HTMLElement | null>(null);

interface SidePanelProps {
  children: ReactNode;
  isOpen: boolean;
  onClose: () => void;
  title: string;
}

/**
 * A page's own drawer (e.g. the order list's filters), docked beside the side menu and pushing the
 * page over. Render it anywhere in the page; it's portalled into SidePanelSlotContext's slot.
 */
export const SidePanel = (props: SidePanelProps) => {
  const { children, isOpen, onClose, title } = props;
  const slot = useContext(SidePanelSlotContext);

  if (!slot) {
    return null;
  }

  const panel = (
    <Box
      aria-hidden={!isOpen}
      component="aside"
      sx={{
        alignSelf: "stretch",
        bgcolor: "background.sidebar",
        boxShadow: (theme) => (isOpen ? toEdgeShadow(theme, "right") : "none"),
        flexShrink: 0,
        overflowX: "hidden",
        position: "relative",
        transition: "width 0.2s ease",
        width: isOpen ? SIDE_PANEL_WIDTH : 0,
        zIndex: 2,
      }}
    >
      {isOpen && (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            height: "100%",
            pb: 1,
            pt: 2,
            px: 2,
            width: SIDE_PANEL_WIDTH,
          }}
        >
          <Stack
            direction="row"
            sx={{
              alignItems: "center",
              mb: 1,
            }}
          >
            <Typography
              sx={{
                flex: 1,
                fontWeight: 600,
              }}
              variant="subtitle1"
            >
              {title}
            </Typography>
            <IconButton
              aria-label="Close"
              onClick={onClose}
              size="small"
              sx={{ position: "relative", left: 6 }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Stack>
          <Box
            sx={{
              display: "flex",
              flex: 1,
              flexDirection: "column",
              minHeight: 0,
            }}
          >
            {children}
          </Box>
        </Box>
      )}
    </Box>
  );

  return createPortal(panel, slot);
};
